export interface Question {
  id: number;
  question: string;
  options: { text: string; description: string }[];
}

export const questions: Question[] = [
  {
    id: 1,
    question: 'Do you know the sales potential of each HCP?',
    options: [
      {
        text: 'No sales data available',
        description:
          'Example: You launch a product in a new therapeutic area; the sales potential of HCPs is not known yet.',
      },
      {
        text: 'Basic sales data available or data only at brick level',
        description:
          'Sales data exists but lacks granularity, possibly aggregated at a local or regional (brick) level. In this case, the doctor level sales potential is still unknown.',
      },
      {
        text: 'Sales data available for most HCPs',
        description:
          'Individual HCP sales data is available for a majority of your target audience. This can be the case if you have script data for some of your HCPs.',
      },
      {
        text: 'Sales data for each HCP available',
        description:
          'Complete and granular sales potential data is available for every target HCP. You can prioritize your sales efforts based on this data.',
      },
    ],
  },
  {
    id: 2,
    question: 'Do you know your Key Opinion Leaders (KOLs) and Digital Opinion Leaders (DOLs)?',
    options: [
      {
        text: 'Neither KOLs nor DOLs identified',
        description: 'Influential figures in the therapy area have not been identified.',
      },
      {
        text: 'I know my KOLs, but missing DOLs',
        description:
          'Traditional key influencers are known, but digital influencers are not identified yet.',
      },
      {
        text: 'Most KOLs and DOLs identified but not rated',
        description:
          'A majority of both traditional and digital influencers are identified but not rated.',
      },
      {
        text: 'All KOLs and DOLs identified and rated',
        description:
          'Comprehensive identification and influence rating for all relevant KOLs and DOLs. You can effectively prioritize your KOL and DOL engagement efforts based on this data.',
      },
    ],
  },
  {
    id: 3,
    question: 'Do you know the channel preference of each HCP?',
    options: [
      {
        text: 'Neither HCP channel preference nor channel acceptance is known',
        description:
          'There is no data on how individual HCPs prefer to receive information (e.g., email, F2F, phone, video chat).',
      },
      {
        text: 'Only channel acceptance for HCPs is known',
        description:
          'Channel acceptance (click rates, open rates..) provides insights on the accepted channels but not on the preferred channels. Professionals often accept channels without actually liking them.',
      },
      {
        text: 'Channel preferences known for most HCPs',
        description:
          'Preferred communication channels are identified for the majority of targeted HCPs.',
      },
      {
        text: 'Channel preference for all HCPs known',
        description:
          'Detailed channel preferences are known for all targeted HCPs. You can effectively customize your communication efforts based on this data.',
      },
    ],
  },
  {
    id: 4,
    question: 'Do you know the communication style of each HCP?',
    options: [
      {
        text: 'HCPs preferred communication style not known',
        description: "There's no data on the communication styles of HCPs.",
      },
      {
        text: 'Anecdotal communication style data available',
        description:
          'Information on communication style is based on informal feedback rather than systematic data collection.',
      },
      {
        text: 'Communication style known for most HCPs',
        description:
          'The preferred communication style is documented for a majority of targeted HCPs.',
      },
      {
        text: 'Communication style known for all HCPs',
        description:
          'Preferred communication style is systematically captured and known for all targeted HCPs. You can effectively customize your communication efforts (style and tone) based on this data.',
      },
    ],
  },
  {
    id: 5,
    question: 'Do you know the persona of each HCP?',
    options: [
      {
        text: 'HCP Persona not defined',
        description:
          'No distinct HCP profiles or archetypes have been created based on behavior, needs, or preferences.',
      },
      {
        text: 'Basic persona segments created',
        description: 'Broad HCP segments have been defined, but lack detailed characteristics.',
      },
      {
        text: 'Detailed personas for key segments created, some HCPs matched to personas',
        description:
          'Rich personas exist for important HCP groups, and some individuals are mapped to these personas.',
      },
      {
        text: 'HCP Persona known for each HCP',
        description:
          'Every targeted HCP is mapped to a well-defined and detailed persona. This allows you to tailor your communication efforts to the specific needs and preferences of each HCP.',
      },
    ],
  },
  {
    id: 7,
    question: 'Do you know the adoption level of each HCP?',
    options: [
      {
        text: 'No adoption level data for HCPs available',
        description:
          'It is unknown where HCPs stand regarding the adoption of new products or therapies (e.g. early adopter, laggard).',
      },
      {
        text: 'Anecdotal adoption level data available',
        description:
          'Information on adoption level is based on informal feedback or rep perception.',
      },
      {
        text: 'Adoption level known for most HCPs',
        description: 'The product adoption level is documented for a majority of targeted HCPs.',
      },
      {
        text: 'Adoption level for all HCPs known',
        description:
          'The product adoption level is systematically captured and known for all targeted HCPs. You can effectively prioritize your HCP engagement efforts based on this data, especially for a new product launch.',
      },
    ],
  },
  {
    id: 6,
    question: 'Do you use your HCP engagement insights?',
    options: [
      {
        text: 'HCP Insights not utilised',
        description:
          'Collected data and insights about HCPs are not used to inform strategy or tactics.',
      },
      {
        text: 'HCP Insights utilised occasionally',
        description:
          'Insights are used sporadically or for specific initiatives, but not systematically.',
      },
      {
        text: 'HCP Insights utilised regularly',
        description:
          'Insights consistently inform marketing and sales activities on a regular basis.',
      },
      {
        text: 'HCP Insights utilised inherently by marketing and sales team',
        description:
          'Using HCP insights is an integral and automatic part of all relevant team workflows and decision-making. You not only have the required HCP data but also effectively use it to optimize your marketing and sales efforts.',
      },
    ],
  },
];
